import { useState, useRef, useEffect, useCallback } from 'react';
import { Save, X, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { polygonsApi, getErrorMessage } from '../../lib/api';

interface BulkMetadataEditorProps {
    polygons: any[];
    onSaveComplete: () => void;
    onNavigateToPolygon?: (id: string) => void;
}

const COMMODITY_OPTIONS = [
    { value: 'cocoa', label: 'Cacao' },
    { value: 'timber', label: 'Madera' },
    { value: 'soy', label: 'Soja' },
    { value: 'palm_oil', label: 'Aceite de Palma' },
    { value: 'coffee', label: 'Café' },
    { value: 'rubber', label: 'Caucho' },
    { value: 'cattle', label: 'Ganado' },
];

const EDITABLE_COLUMNS = [
    { key: 'name', label: 'Nombre', type: 'text' as const },
    { key: 'producer_name', label: 'Productor', type: 'text' as const },
    { key: 'legal_name', label: 'Nombre Legal', type: 'text' as const },
    { key: 'organization', label: 'Organización', type: 'text' as const },
    { key: 'commodity', label: 'Commodity', type: 'select' as const },
    { key: 'country', label: 'País', type: 'text' as const },
    { key: 'region', label: 'Región', type: 'text' as const },
];

const READONLY_COLUMNS = [
    { key: 'area_ha', label: 'Área (ha)' },
    { key: 'status', label: 'Estado' },
];

export default function BulkMetadataEditor({ polygons, onSaveComplete, onNavigateToPolygon }: BulkMetadataEditorProps) {
    const [editState, setEditState] = useState<Record<string, Record<string, string>>>({});
    const [activeCell, setActiveCell] = useState<{ rowId: string; field: string } | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
    const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

    const dirtyRowIds = Object.keys(editState);
    const hasChanges = dirtyRowIds.length > 0;

    // Focus input when activeCell changes
    useEffect(() => {
        if (activeCell && inputRef.current) {
            inputRef.current.focus();
            if (inputRef.current instanceof HTMLInputElement) {
                inputRef.current.select();
            }
        }
    }, [activeCell]);

    const getCellValue = (polygon: any, field: string): string => {
        return editState[polygon.id]?.[field] ?? polygon[field] ?? '';
    };

    const isDirty = (polygonId: string, field: string): boolean => {
        return editState[polygonId]?.[field] !== undefined;
    };

    const setCellValue = useCallback((polygonId: string, field: string, value: string) => {
        const polygon = polygons.find((p) => p.id === polygonId);
        if (!polygon) return;

        const originalValue = polygon[field] ?? '';
        setEditState((prev) => {
            const rowEdits = { ...(prev[polygonId] || {}) };

            if (value === originalValue) {
                delete rowEdits[field];
            } else {
                rowEdits[field] = value;
            }

            const next = { ...prev };
            if (Object.keys(rowEdits).length === 0) {
                delete next[polygonId];
            } else {
                next[polygonId] = rowEdits;
            }
            return next;
        });
    }, [polygons]);

    const navigateCell = useCallback((rowId: string, field: string, direction: 'next' | 'prev' | 'down') => {
        const rowIdx = polygons.findIndex((p) => p.id === rowId);
        const colIdx = EDITABLE_COLUMNS.findIndex((c) => c.key === field);
        if (rowIdx === -1 || colIdx === -1) return;

        let nextRow = rowIdx;
        let nextCol = colIdx;

        if (direction === 'next') {
            nextCol++;
            if (nextCol >= EDITABLE_COLUMNS.length) {
                nextCol = 0;
                nextRow++;
            }
        } else if (direction === 'prev') {
            nextCol--;
            if (nextCol < 0) {
                nextCol = EDITABLE_COLUMNS.length - 1;
                nextRow--;
            }
        } else if (direction === 'down') {
            nextRow++;
        }

        if (nextRow >= 0 && nextRow < polygons.length) {
            setActiveCell({ rowId: polygons[nextRow].id, field: EDITABLE_COLUMNS[nextCol].key });
        } else {
            setActiveCell(null);
        }
    }, [polygons]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent, rowId: string, field: string) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            navigateCell(rowId, field, e.shiftKey ? 'prev' : 'next');
        } else if (e.key === 'Enter') {
            e.preventDefault();
            navigateCell(rowId, field, 'down');
        } else if (e.key === 'Escape') {
            e.preventDefault();
            // Revert this cell
            setEditState((prev) => {
                const rowEdits = { ...(prev[rowId] || {}) };
                delete rowEdits[field];
                const next = { ...prev };
                if (Object.keys(rowEdits).length === 0) {
                    delete next[rowId];
                } else {
                    next[rowId] = rowEdits;
                }
                return next;
            });
            setActiveCell(null);
        }
    }, [navigateCell]);

    const handleSave = async () => {
        // Validate: name must not be empty
        for (const [polygonId, changes] of Object.entries(editState)) {
            if (changes.name !== undefined && changes.name.trim() === '') {
                const polygon = polygons.find((p) => p.id === polygonId);
                toast.error(`El nombre no puede estar vacío (${polygon?.name || polygonId})`);
                setActiveCell({ rowId: polygonId, field: 'name' });
                return;
            }
        }

        setSaving(true);
        setSaveErrors({});

        const updates = Object.entries(editState).map(([polygonId, changes]) => ({
            polygonId,
            changes: Object.fromEntries(
                Object.entries(changes).map(([k, v]) => [k, v.trim() || undefined])
            ),
        }));

        const results = await Promise.allSettled(
            updates.map(({ polygonId, changes }) => polygonsApi.update(polygonId, changes))
        );

        const errors: Record<string, string> = {};
        const succeededIds: string[] = [];

        results.forEach((result, i) => {
            if (result.status === 'fulfilled') {
                succeededIds.push(updates[i].polygonId);
            } else {
                errors[updates[i].polygonId] = getErrorMessage(result.reason) || 'Error al guardar';
            }
        });

        // Remove succeeded rows from editState
        setEditState((prev) => {
            const next = { ...prev };
            succeededIds.forEach((id) => delete next[id]);
            return next;
        });

        setSaveErrors(errors);
        setSaving(false);

        if (succeededIds.length > 0) {
            toast.success(`${succeededIds.length} polígono(s) actualizado(s)`);
            onSaveComplete();
        }
        if (Object.keys(errors).length > 0) {
            toast.error(`${Object.keys(errors).length} polígono(s) fallaron`);
        }
    };

    const handleDiscard = () => {
        setEditState({});
        setSaveErrors({});
        setActiveCell(null);
    };

    const commodityLabel = (value: string) =>
        COMMODITY_OPTIONS.find((o) => o.value === value)?.label || value;

    return (
        <div className="card overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        Haz clic en una celda para editarla. Usa <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Tab</kbd> para navegar, <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Enter</kbd> para bajar, <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Esc</kbd> para cancelar.
                    </p>
                    {hasChanges && (
                        <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                            {dirtyRowIds.length} polígono(s) modificado(s)
                        </span>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto max-h-[calc(100vh-320px)] overflow-y-auto">
                <table className="w-full min-w-[800px] text-sm">
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                            <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-8">
                                #
                            </th>
                            {EDITABLE_COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                >
                                    {col.label}
                                </th>
                            ))}
                            {READONLY_COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className="px-3 py-2.5 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {polygons.map((polygon, rowIdx) => {
                            const rowHasError = !!saveErrors[polygon.id];
                            const rowIsDirty = !!editState[polygon.id];
                            return (
                                <tr
                                    key={polygon.id}
                                    className={`border-b border-gray-100 dark:border-gray-700/50 transition-colors ${
                                        rowHasError
                                            ? 'bg-red-50 dark:bg-red-900/10'
                                            : rowIsDirty
                                            ? 'bg-amber-50/30 dark:bg-amber-900/5'
                                            : 'hover:bg-gray-50/50 dark:hover:bg-gray-800/30'
                                    }`}
                                >
                                    {/* Row number */}
                                    <td className="px-3 py-2 text-xs text-gray-400 font-mono select-none">
                                        {rowIdx + 1}
                                        {rowHasError && (
                                            <span title={saveErrors[polygon.id]} className="ml-1">
                                                <AlertCircle className="w-3 h-3 text-red-500 inline" />
                                            </span>
                                        )}
                                    </td>

                                    {/* Editable cells */}
                                    {EDITABLE_COLUMNS.map((col) => {
                                        const isActive = activeCell?.rowId === polygon.id && activeCell?.field === col.key;
                                        const cellDirty = isDirty(polygon.id, col.key);
                                        const value = getCellValue(polygon, col.key);

                                        return (
                                            <td
                                                key={col.key}
                                                className={`relative ${
                                                    isActive
                                                        ? 'p-0'
                                                        : `px-3 py-2 cursor-pointer ${
                                                              cellDirty
                                                                  ? 'bg-amber-50 dark:bg-amber-900/20 border-l-2 border-l-amber-400'
                                                                  : 'hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
                                                          }`
                                                }`}
                                                onClick={() => {
                                                    if (!isActive && !saving) {
                                                        setActiveCell({ rowId: polygon.id, field: col.key });
                                                    }
                                                }}
                                            >
                                                {isActive ? (
                                                    col.type === 'select' ? (
                                                        <select
                                                            ref={(el) => { inputRef.current = el; }}
                                                            value={value}
                                                            onChange={(e) => {
                                                                setCellValue(polygon.id, col.key, e.target.value);
                                                                navigateCell(polygon.id, col.key, 'down');
                                                            }}
                                                            onKeyDown={(e) => handleKeyDown(e, polygon.id, col.key)}
                                                            onBlur={() => setActiveCell(null)}
                                                            className="w-full px-3 py-2 border-2 border-primary-500 outline-none bg-white dark:bg-gray-900 text-sm"
                                                        >
                                                            {COMMODITY_OPTIONS.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            ref={(el) => { inputRef.current = el; }}
                                                            type="text"
                                                            value={value}
                                                            onChange={(e) => setCellValue(polygon.id, col.key, e.target.value)}
                                                            onKeyDown={(e) => handleKeyDown(e, polygon.id, col.key)}
                                                            onBlur={() => setActiveCell(null)}
                                                            className="w-full px-3 py-2 border-2 border-primary-500 outline-none bg-white dark:bg-gray-900 text-sm"
                                                        />
                                                    )
                                                ) : col.key === 'name' && onNavigateToPolygon ? (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onNavigateToPolygon(polygon.id); }}
                                                        className="block truncate text-left text-primary-600 hover:text-primary-800 dark:text-primary-400 hover:underline font-medium"
                                                        title="Ver en el mapa"
                                                    >
                                                        {value || '—'}
                                                    </button>
                                                ) : (
                                                    <span className={`block truncate ${!value ? 'text-gray-400 italic' : 'text-gray-900 dark:text-white'}`}>
                                                        {col.type === 'select' ? commodityLabel(value) : value || '—'}
                                                    </span>
                                                )}
                                            </td>
                                        );
                                    })}

                                    {/* Readonly cells */}
                                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-right font-mono">
                                        {polygon.area_ha?.toFixed(2) || '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span
                                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                                polygon.status === 'active'
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                            }`}
                                        >
                                            {polygon.status || '—'}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Save bar */}
            {hasChanges && (
                <div className="sticky bottom-0 bg-primary-600 text-white px-4 py-3 flex items-center justify-between border-t border-primary-700 animate-fade-in">
                    <span className="text-sm font-medium">
                        {dirtyRowIds.length} polígono(s) modificado(s)
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDiscard}
                            disabled={saving}
                            className="flex items-center gap-1.5 text-sm bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded transition-colors"
                        >
                            <X className="w-4 h-4" />
                            Descartar
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-1.5 text-sm bg-white text-primary-700 hover:bg-primary-50 px-4 py-1.5 rounded font-medium transition-colors"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            Guardar cambios
                        </button>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {polygons.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                    No hay polígonos para editar
                </div>
            )}
        </div>
    );
}
